import { Injectable } from '@angular/core';
import { AbstractControl, UntypedFormControl } from '@angular/forms';
import { TranslocoService } from '@ngneat/transloco';
import { firstValueFrom } from 'rxjs';

@Injectable({
  providedIn: 'root',
})
/**
 * Handles validation error messages
 */
export class ValidationErrorService {
  /**
   * @constructor
   */
  constructor(private translocoService: TranslocoService) {}

  /**
   * a huge string to set baseline for maximum input length
   */
  private get hugeString() {
    return 'a'.repeat(100);
  }

  /**
   * Defines a base maximum input length for input fields of this app
   */
  private get maxInputLength() {
    return 255;
  }

  /**
   * Translates errors
   * @param {AbstractControl} abstractControl
   * @param {string} errorType
   * @param {string} fieldName
   * @return {Promise<string>}
   */
  async translateError(
    abstractControl: AbstractControl,
    errorType?: string,
    fieldName?: string
  ): Promise<string> {
    const errors = abstractControl.errors;
    if (!errors) return '';

    let errorMessage = '';
    let moreInfo = {};

    if (errors['matDatepickerParse'] || errors['invalidDate']) {
      errorMessage = 'invalid-calendar-date';
      return firstValueFrom(
        this.translocoService.selectTranslate(errorMessage, moreInfo)
      );
    }

    for (const errorName in errors) {
      if (Object.prototype.hasOwnProperty.call(errors, errorName)) {
        const errorObject = errors[errorName];
        errorMessage = this.resolveErrorMessage(
          errorName,
          errorObject,
          errorType
        );
        moreInfo = this.resolveMoreInfo(errorName, errorObject, fieldName);
        break;
      }
    }
    return firstValueFrom(
      this.translocoService.selectTranslate(errorMessage, moreInfo)
    );
  }

  /**
   * @param {string} errorName
   * @param {any} errorObject
   * @param {string} errorType
   * @return {string}
   */
  private resolveErrorMessage(
    errorName: string,
    errorObject: any,
    errorType?: string
  ): string {
    if (typeof errorObject === 'string') {
      return errorObject;
    }
    return errorName === 'mask'
      ? `invalid-${errorType}`
      : `invalid-${errorName}`;
  }

  /**
   * @param {string} errorName
   * @param {any} errorObject
   * @param {string} fieldName
   * @return {object}
   */
  private resolveMoreInfo(
    errorName: string,
    errorObject: any,
    fieldName?: string
  ): object {
    if (
      errorName === 'minlength' ||
      errorName === 'maxlength' ||
      errorName === 'minimumTrimmedLength'
    ) {
      return { requiredLength: errorObject.requiredLength };
    }
    if (errorName === 'min' || errorName === 'max') {
      return { field: fieldName, requiredLength: errorObject[errorName] };
    }
    return {};
  }

  /**
   * Uses abstract control information to decide proper max length of field
   * @param {AbstractControl} abstractControl
   * @return {number}
   */
  getMaximumLength(abstractControl: AbstractControl): number {
    const validators: any[] = (abstractControl as any)._rawValidators;
    if (!validators || typeof validators[Symbol.iterator] !== 'function') {
      return this.maxInputLength;
    }
    for (const validator of validators) {
      const errors = validator(new UntypedFormControl(this.hugeString));
      const maxLength = errors?.maxlength?.requiredLength;
      if (maxLength) {
        return maxLength;
      }
    }
    return this.maxInputLength;
  }

  /**
   * Discovers all errors a abstrac control might possess
   * @param {AbstractControl} abstractControl
   * @return {string[]}
   */
  getValidators(abstractControl: AbstractControl): string[] {
    const validators: any[] = (abstractControl as any)._rawValidators;
    return validators.reduce((prev, curr) => {
      const fieldErrors = curr(new UntypedFormControl(''));
      prev.push(...Object.keys(fieldErrors));
      return prev;
    }, []);
  }
}
